import type { AgentIdentity } from "./types";

/**
 * TA-345. Whether an agent identity attributes anything, decided before the
 * database has to.
 *
 * Section 5: every row carries `agent_id` and an agent version, because an
 * action nobody can attribute is impossible to attribute later. Since
 * migration 0023 the database refuses a blank one on events, drafts,
 * spend_ledger and telemetry_calls, each column held to
 * `CHECK (col ~ '[^[:space:]]')`. TA-231 left the application side open: "no
 * `AgentIdentity` constructor or gateway check refuses a blank before the
 * database does", so a blank identity reached the gateway, was screened,
 * routed and sent, and was refused only at the row, after the call.
 *
 * Blank is: no character outside Unicode's White_Space property, the 25 code
 * points written out below. The CHECK has no one rule to copy. Postgres
 * answers `[:space:]` with the C library's iswspace under the database's
 * LC_CTYPE, so the class belongs to the server, and it is the six ASCII
 * spaces only in the C locale. Measured by scanning every code point
 * (FINDINGS TA-345):
 *   - PGlite, the unit suites' database (C.UTF-8, a musl-derived libc): 21,
 *     the six plus U+0085, U+2000-2006, U+2008-200A, U+2028, U+2029, U+205F
 *     and U+3000.
 *   - embedded-postgres on macOS under en_US.UTF-8, the local integration
 *     server: 24, the six plus U+00A0, U+1680, U+2000-200A, U+2028, U+2029,
 *     U+202F, U+205F and U+3000.
 * RDS runs glibc, read (not measured) as the six plus U+1680, U+2000-2006,
 * U+2008-200A, U+2028, U+2029, U+205F and U+3000. Each class is a subset of
 * White_Space and together they cover all of it, so no identity this guard
 * serves is refused by any of those servers' CHECKs, which is the defect
 * TA-345 removes. The price is the other direction: each server would store a
 * few White_Space-only identities this refuses (on PGlite, U+00A0, U+1680,
 * U+2007 and U+202F alone). An identity of nothing but whitespace attributes
 * nothing on any reading, so that refusal is the loud side, not a loss.
 *
 * Not JavaScript's `\s`, which differs from White_Space in both directions:
 * it misses U+0085, which PGlite's CHECK refuses, and matches U+FEFF, which no
 * server's class holds. Nothing is trimmed: the database stores " reply "
 * verbatim, and a format rule for identities is unruled (TA-231 assumption 2).
 */

/** The two fields an identity has, in the order a refusal names them. */
export type AgentIdentityField = "agentId" | "agentVersion";

const AGENT_IDENTITY_FIELDS: readonly AgentIdentityField[] = ["agentId", "agentVersion"];

/**
 * Any character outside Unicode's White_Space property, written out rather
 * than `\s` or `\p{White_Space}` so the set is fixed here, not by the
 * runtime's Unicode data. agentIdentity.test.ts holds it to
 * `\p{White_Space}` code point for code point.
 */
const NOT_WHITE_SPACE =
  /[^\t\n\v\f\r \u0085\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]/u;

/**
 * The fields of `agent` that are blank, in declaration order; empty when the
 * identity is attributable.
 *
 * The type admits only strings, and a cast or a JS caller admits anything, so
 * `typeof` comes first: a value that is not a string is blank, and a missing
 * identity is blank in both fields. Neither throws, so a caller can refuse the
 * request instead of failing on the way to refusing it.
 */
export function blankAgentIdentityFields(agent: AgentIdentity): readonly AgentIdentityField[] {
  const raw: unknown = agent;
  if (typeof raw !== "object" || raw === null) return [...AGENT_IDENTITY_FIELDS];
  const fields = raw as Readonly<Record<AgentIdentityField, unknown>>;
  return AGENT_IDENTITY_FIELDS.filter((field) => {
    const value = fields[field];
    return typeof value !== "string" || !NOT_WHITE_SPACE.test(value);
  });
}

/** True when either field is blank: nothing in it but White_Space (TA-345). */
export function isBlankAgentIdentity(agent: AgentIdentity): boolean {
  return blankAgentIdentityFields(agent).length > 0;
}
